// Single source of truth for the platform / gallery a project was released
// through. Powers both the "View on <platform>" link and the platform badge.
//
// A project is keyed by `${contractAddress}-${projectId}` (lowercased). The
// 1/1 marketplace works carry their own `platform` field (see marketplaceWorks).

import { normalizeContractAddress } from "utils/artBlocksUrls"
import { MarketplaceWork } from "data/marketplaceWorks"

export type PlatformId =
  | "art-blocks"
  | "bright-moments"
  | "superrare"
  | "foundation"
  | "plottables"
  | "verse"

export interface PlatformLink {
  /** e.g. "View on Art Blocks" */
  label: string
  href: string
}

export interface PlatformBadge {
  id: PlatformId
  /** Short badge text, e.g. "AB500", "Bright Moments". */
  label: string
  /** Hover/aria context, e.g. "Part of the Art Blocks 500". */
  tooltip: string
  /** Optional click target. */
  href?: string
}

type ProjectId = string | number | BigInt

const AB500_DISCOVER_URL = "https://www.artblocks.io/discover/ab-500"

type ProjectKind = "art-blocks-flagship" | "art-blocks-engine" | "bright-moments" | "plottables"

interface ProjectPlatformConfig {
  kind: ProjectKind
  /** Member of the Art Blocks 500 list (artblocks.io/discover/ab-500). */
  ab500?: boolean
  /** Bright Moments collection slug for per-piece links. */
  slug?: string
}

// contract-projectId (lowercased) -> platform classification
const PROJECT_PLATFORMS: Record<string, ProjectPlatformConfig> = {
  // Art Blocks flagship — Art Blocks 500
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-156": { kind: "art-blocks-flagship", ab500: true }, // Moments
  "0xa7d8d9ef8d8ce8992df33d8b8cf4aebabd5bd270-222": { kind: "art-blocks-flagship", ab500: true }, // Pieces of Me
  // Bright Moments (on Art Blocks Engine, Bright Moments brand)
  "0x0a1bbd57033f57e7b6743621b79fcb9eb2ce3676-32": { kind: "bright-moments", slug: "color-streams" }, // Color Streams
  // Plottables (on Art Blocks Engine, Plottables brand)
  "0xa319c382a702682129fcbf55d514e61a16f97f9c-15": { kind: "plottables" }, // Shields
  // r4v3n's own Art Blocks Engine
  "0xd9b7ec74c06c558a59afde6a16e614950730f44d-0": { kind: "art-blocks-engine" }, // Marfa: Middle of Somewhere
  "0xd9b7ec74c06c558a59afde6a16e614950730f44d-1": { kind: "art-blocks-engine" }, // Marfa: Outtakes
  "0x47a91457a3a1f700097199fd63c039c4784384ab-301": { kind: "art-blocks-engine" }, // Dots (Arbitrum)
  "0x0d39ab55664007ff2d089a25480f169c6d0597bb-0": { kind: "art-blocks-engine" }, // Dots: Composites (Arbitrum)
}

/** Art Blocks token id for a project's first piece (projectId * 1e6). */
const artBlocksFirstTokenId = (projectId: ProjectId) => String(Number(projectId) * 1_000_000)

const artBlocksBadge = (
  engine: boolean,
  chainId: number,
  contractAddress: string,
  tokenId: string
): PlatformBadge => ({
  id: "art-blocks",
  label: "Art Blocks",
  tooltip: engine ? "On Art Blocks Engine" : "Released on Art Blocks",
  href: artBlocksTokenUrl(chainId, contractAddress, tokenId),
})

const artBlocksLink = (chainId: number, contractAddress: string, tokenId: string): PlatformLink => ({
  label: "Art Blocks",
  href: artBlocksTokenUrl(chainId, contractAddress, tokenId),
})

const projectKey = (contractAddress: string, projectId: ProjectId) =>
  `${normalizeContractAddress(contractAddress)}-${projectId}`

const getProjectConfig = (contractAddress?: string, projectId?: ProjectId) => {
  if (!contractAddress || projectId === undefined || projectId === null) return undefined
  return PROJECT_PLATFORMS[projectKey(contractAddress, projectId)]
}

const artBlocksTokenUrl = (chainId: number, contractAddress: string, tokenId: string) =>
  `https://www.artblocks.io/token/${chainId}/${normalizeContractAddress(contractAddress)}/${tokenId}`

interface ProjectLinkParams {
  contractAddress: string
  projectId: ProjectId
  tokenId: string
  invocation?: number | string
  chainId?: number
}

/**
 * "View on <platform>" links for a project token. Partner projects on Art
 * Blocks Engine return both the partner link and the Art Blocks link (partner
 * first); flagship / r4v3n-Engine projects return just the Art Blocks link.
 */
export const getProjectPlatformLinks = ({
  contractAddress,
  projectId,
  tokenId,
  invocation,
  chainId = 1,
}: ProjectLinkParams): PlatformLink[] => {
  const config = getProjectConfig(contractAddress, projectId)
  if (!config) return []

  const ab = artBlocksLink(chainId, contractAddress, tokenId)
  switch (config.kind) {
    case "art-blocks-flagship":
    case "art-blocks-engine":
      return [ab]
    case "bright-moments":
      return [
        {
          label: "Bright Moments",
          href: `https://app.brightmoments.io/collections/${config.slug}${
            invocation !== undefined ? `/${invocation}` : ""
          }`,
        },
        ab,
      ]
    case "plottables":
      return [{ label: "Plottables", href: "https://plottables.io/" }, ab]
    default:
      return []
  }
}

/**
 * Platform / curation badges for a project. Partner projects on Art Blocks
 * Engine get the partner badge plus an "Art Blocks" infrastructure badge
 * (partner first); flagship AB500 projects get a single AB500 badge; r4v3n's
 * own Engine projects get a single "Art Blocks" badge.
 */
export const getProjectPlatformBadges = (
  contractAddress?: string,
  projectId?: ProjectId,
  opts?: { chainId?: number; tokenId?: string }
): PlatformBadge[] => {
  const config = getProjectConfig(contractAddress, projectId)
  if (!config || !contractAddress || projectId === undefined || projectId === null) return []

  const chainId = opts?.chainId ?? 1
  const tokenId = opts?.tokenId ?? artBlocksFirstTokenId(projectId)
  const ab = artBlocksBadge(true, chainId, contractAddress, tokenId)

  switch (config.kind) {
    case "art-blocks-flagship":
      return [
        config.ab500
          ? { id: "art-blocks", label: "AB500", tooltip: "Part of the Art Blocks 500", href: AB500_DISCOVER_URL }
          : artBlocksBadge(false, chainId, contractAddress, tokenId),
      ]
    case "bright-moments":
      return [
        {
          id: "bright-moments",
          label: "Bright Moments",
          tooltip: "Released on Bright Moments via Art Blocks Engine",
          href: `https://app.brightmoments.io/collections/${config.slug}`,
        },
        ab,
      ]
    case "plottables":
      return [
        {
          id: "plottables",
          label: "Plottables",
          tooltip: "Released on Plottables via Art Blocks Engine",
          href: "https://plottables.io/",
        },
        ab,
      ]
    case "art-blocks-engine":
      return [ab]
    default:
      return []
  }
}

// ---- 1/1 marketplace works -------------------------------------------------

export const getMarketplaceWorkPlatformLink = (work: MarketplaceWork): PlatformLink | undefined => {
  if (work.platform === "SuperRare") return { label: "SuperRare", href: work.externalUrl }
  if (work.platform === "Verse") return { label: "Verse", href: work.externalUrl }
  // Foundation (and anything else) intentionally omitted.
  return undefined
}

export const getMarketplaceWorkPlatformBadge = (work: MarketplaceWork): PlatformBadge | undefined => {
  if (work.platform === "SuperRare") {
    return { id: "superrare", label: "SuperRare", tooltip: "Released on SuperRare", href: work.externalUrl }
  }
  if (work.platform === "Verse") {
    return { id: "verse", label: "Verse", tooltip: "Released on Verse", href: work.externalUrl }
  }
  return undefined
}
